import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { ApiResponse } from "~/types/ApiResponse";
import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import {
    faArrowsRotate,
    faStar,
    faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { faMapLocation, faShare } from "@fortawesome/pro-light-svg-icons";
import { useClipboard } from "#imports";

export interface Location {
    id: number;
    name: string;
    phone_number: string;
    website: string;
    google_place_id: string;
    business_status: string;
    geo_location: GeoLocation;
    geo_view_high: GeoViewHigh;
    geo_view_low: GeoViewLow;
    types: string[];
    metadata: Metadata;
    address: Address;
    country: Country;
    language: Language;
    locale: Locale;
    timezone: string;
    google_review_url: string | null;
    reviews: GoogleReview[];
    last_snapshot_at: string;
    created_at: string;
    updated_at: string;
}

export interface GeoLocation {
    longitude: number;
    latitude: number;
}

export interface GeoViewHigh {
    longitude: number;
    latitude: number;
}

export interface GeoViewLow {
    longitude: number;
    latitude: number;
}

export interface Metadata {
    original_name: string;
    rating: number;
    user_rating_count: number;
    current_opening_hours: any[];
    google_places_details: GooglePlacesDetails;
    regular_opening_hours: any[];
}

export interface GoogleReview {
    name?: string;
    relative_publish_time_description?: string;
    rating: number;
    text: {
        languageCode: string;
        text: string;
    };
    original_text: {
        languageCode: string;
        text: string;
    };
    author: {
        displayName: string;
        uri: string;
        photoUri: string;
    };
    publish_time?: string;
    review_url?: string;
}

export interface GooglePlacesDetails {
    maps_uri: string;
    plus_code: PlusCode;
}

export interface PlusCode {
    globalCode: string;
    compoundCode: string;
}

export interface Address {
    id: number;
    address1: any;
    address2: any;
    city: string;
    company: any;
    country: string;
    country_code: string;
    name: string;
    phone: string;
    province: string;
    province_code: string;
    street: string;
    summary: any;
    url: string;
    zip: string;
    street_number: string;
    sub_locality: string;
    metadata: unknown;
}

export interface Country {
    id: number;
    code: string;
    name: string;
    alpha_2: string;
    alpha_3: string;
    iso_3166_2: string;
    region: string;
    sub_region: string;
    intermediate_region: string;
    region_code: string;
    sub_region_code: string;
    intermediate_region_code: string;
    dial_code: number;
}

export interface Language {
    id: number;
    code: string;
    name: string;
    ISO_639_1: string;
    ISO_639_2T: string;
    ISO_639_2B: string;
    scope: string;
    type: string;
}

export interface Locale {
    id: number;
    code: string;
    language_id: number;
    iso_script_id: any;
    country_id: number;
    name: string;
    is_known: number;
    language: Language;
    country: Country;
    iso_script: any;
}

export interface Language {
    id: number;
    code: string;
    name: string;
    ISO_639_1: string;
    ISO_639_2T: string;
    ISO_639_2B: string;
    scope: string;
    type: string;
}

export interface Country {
    id: number;
    code: string;
    name: string;
    alpha_2: string;
    alpha_3: string;
    iso_3166_2: string;
    dial_code: number;
    region: string;
    sub_region: string;
    intermediate_region: string;
    region_code: string;
    sub_region_code: string;
    intermediate_region_code: string;
}

export default class LocationService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Location[]> {
        return this.paginate<Location[]>(
            this.route("api.locations.index", "/locations"),
            options as PaginatedFetchOptions,
        );
    }

    public find(
        id: number,
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<Location> {
        return this.enhancedFetch<Location>(
            this.route("api.locations.show", "/locations/{location}", {
                location: id,
            }),
            options,
        );
    }

    show(location_id: number) {
        return this.request(
            this.route("api.locations.show", "/locations/{location}", {
                location: location_id,
            }),
            {
                method: "GET",
            },
        );
    }

    public all(options?: EnhancedFetchOptions): EnhancedFetch<Location[]> {
        return this.enhancedFetch<Location[]>(
            this.route("api.locations.all", "/locations/all"),
            options,
        );
    }

    public listPlaceIds(options?: EnhancedFetchOptions): EnhancedFetch<[]> {
        return this.enhancedFetch<[]>(
            this.route("api.locations.google-place-ids", "/google-place-ids"),
            options,
        );
    }

    public update(
        id: string,
        data: Partial<Location>,
    ): Promise<ApiResponse<Location>> {
        return this.request<ApiResponse<Location>>(
            this.route("api.locations.update", "/locations/{location}", {
                location: id,
            }),
            {
                method: "POST",
                body: jsonToFormData(data),
                query: {
                    _method: "PATCH",
                },
            },
        ).then(async (location) => {
            const { user, refreshIdentity } = useAuth();

            if (location.data.id === user.value?.account?.location?.id) {
                await refreshIdentity();
            }

            return location;
        });
    }

    public delete(id: number): Promise<null> {
        return this.request(
            this.route("api.locations.destroy", "/locations/{location}", {
                location: id,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public async create(
        value: Partial<Location>,
    ): Promise<ApiResponse<Location>> {
        return this.request(this.route("api.locations.store", "/locations"), {
            method: "POST",
            body: value,
        });
    }

    public edit(location: Location) {
        useLocation().value = location;

        navigateTo({
            name: "settings-account-locations-id",
            params: {
                id: location.id,
            },
        });
    }

    public dropdown(
        location: Location,
        options: {
            onDelete?: () => void;
            onSync?: () => void;
        } = {},
    ) {
        const lists: Array<Array<Record<string, any>>> = [
            [
                {
                    label: "Edit",
                    icon: faEdit,
                    click: () => this.edit(location),
                },
            ],
        ];

        const { user } = useUser();
        const api = useApi();

        if (user.value?.account?.location?.id !== location.id) {
            const setDefaultLocation = (id: number, original_id?: number) => {
                api.account
                    .update({
                        location_id: id,
                    })
                    .then(async () => {
                        useToast().add(
                            original_id !== undefined
                                ? {
                                      title: "Default Location Updated",
                                      description: `${location.name} is now your default location.`,
                                      actions: [
                                          {
                                              label: "Undo",
                                              click: () => {
                                                  setDefaultLocation(
                                                      original_id as number,
                                                  );
                                              },
                                          },
                                      ],
                                  }
                                : {
                                      title: "Default Location Restored",
                                  },
                        );

                        await useAuth().refreshIdentity();
                    });
            };

            lists[0].push({
                label: "Set Default",
                icon: faStar,
                click: () =>
                    setDefaultLocation(
                        location.id,
                        (user.value?.account?.location?.id as number) ?? null,
                    ),
            });
        }

        if (options.onSync) {
            lists[0].push({
                label: "Import reviews from Google",
                icon: faArrowsRotate,
                click: () => (options.onSync as () => void)(),
            });
        }

        if (location.metadata?.google_places_details?.maps_uri) {
            lists[0].splice(0, 0, {
                label: "Google Maps",
                icon: faMapLocation,
                click: () =>
                    window.open(
                        location.metadata?.google_places_details?.maps_uri,
                        "_blank",
                    ),
            });
        }

        if (location.google_review_url) {
            lists[0].splice(0, 0, {
                label: "Share Google Review URL",
                icon: faShare,
                click: () => {
                    const { copy } = useClipboard({
                        source: location.google_review_url as string,
                        legacy: true,
                    });

                    copy(location.google_review_url as string);

                    useToast().add({
                        title: "Review URL Copied",
                        actions: [
                            {
                                label: "Open URL",
                                click: () =>
                                    window.open(
                                        location.google_review_url as string,
                                        "_blank",
                                    ),
                            },
                        ],
                    });
                },
            });
        }

        if (options.onDelete) {
            lists.push([
                {
                    label: "Delete",
                    icon: faTrash,
                    class: "text-red-500",
                    click: () => (options.onDelete as () => void)(),
                },
            ]);
        }

        return lists;
    }
}
